/**
 * Wraps a chart with a "Read chart" menu: speak a plain-language summary (speechSynthesis),
 * show the underlying numbers as a table, or copy the summary. The summary is also the
 * chart's accessible description, so screen readers get it without opening the menu.
 */
import { Check, Copy, MoreHorizontal, Square, Table2, Volume2 } from "lucide-react";
import { useCallback, useEffect, useId, useState } from "react";
import { Button } from "../../components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/table";
import { cn } from "../../lib/utils";

export interface ChartTableColumn {
  key: string;
  label: string;
  numeric?: boolean;
}

export interface ChartFrameProps {
  /** Spoken / accessible name for the chart (falls back to "Chart"). */
  title?: string;
  /** Plain-language summary of what the chart shows. */
  summary: string;
  columns: ChartTableColumn[];
  rows: Record<string, unknown>[];
  formatCell?: (key: string, value: unknown) => string;
  height: number;
  className?: string;
  slot: string;
  children: React.ReactNode;
}

function speechAvailable(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

/** Speak `text`, cancelling anything already queued; resolves the returned `stop` on end. */
function useSpeech(text: string) {
  const [speaking, setSpeaking] = useState(false);
  const stop = useCallback(() => {
    if (speechAvailable()) window.speechSynthesis.cancel();
    setSpeaking(false);
  }, []);
  const speak = useCallback(() => {
    if (!speechAvailable()) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    setSpeaking(true);
    window.speechSynthesis.speak(u);
  }, [text]);
  useEffect(() => stop, [stop]);
  return { speaking, speak, stop, supported: speechAvailable() };
}

export function ChartFrame({
  title,
  summary,
  columns,
  rows,
  formatCell,
  height,
  className,
  slot,
  children,
}: ChartFrameProps) {
  const id = useId();
  const [showTable, setShowTable] = useState(false);
  const [copied, setCopied] = useState(false);
  const speech = useSpeech(summary);
  const name = title ?? "Chart";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div data-slot={slot} className={cn("group/chart relative w-full", className)}>
      <p id={`${id}-desc`} className="sr-only">
        {summary}
      </p>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Read chart: ${name}`}
            className="absolute -top-1 right-0 z-10 size-7 text-muted-foreground opacity-60 transition-opacity duration-150 hover:opacity-100 focus-visible:opacity-100 group-hover/chart:opacity-100"
          >
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {speech.supported ? (
            speech.speaking ? (
              <DropdownMenuItem onSelect={speech.stop}>
                <Square /> Stop reading
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={speech.speak}>
                <Volume2 /> Read aloud
              </DropdownMenuItem>
            )
          ) : null}
          <DropdownMenuItem onSelect={() => setShowTable((v) => !v)}>
            <Table2 /> {showTable ? "Show chart" : "Show as table"}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={copy}>
            {copied ? <Check /> : <Copy />} Copy summary
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {showTable ? (
        <div className="overflow-auto rounded-lg border" style={{ maxHeight: height }}>
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((c) => (
                  <TableHead key={c.key} className={c.numeric ? "text-right" : undefined}>
                    {c.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={String(r[columns[0]?.key ?? ""] ?? i)}>
                  {columns.map((c) => (
                    <TableCell key={c.key} className={c.numeric ? "text-right tabular" : undefined}>
                      {formatCell ? formatCell(c.key, r[c.key]) : String(r[c.key] ?? "")}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div
          role="img"
          aria-label={name}
          aria-describedby={`${id}-desc`}
          className="w-full"
          style={{ height }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
